import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notificationsApi } from "@/lib/api/notifications";
import { showToast } from "@/lib/toast";
import {
  Notification,
  NotificationListResponse,
  UnreadCountResponse,
} from "@/types/notification";

// Query keys para React Query
const notificationKeys = {
  all: ["notifications"] as const,
  list: (page: number, pageSize: number, unreadOnly: boolean) =>
    [...notificationKeys.all, "list", page, pageSize, unreadOnly] as const,
  unreadCount: () => [...notificationKeys.all, "unread-count"] as const,
};

/**
 * Hook para listar notificaciones con paginación.
 *
 * @param page - Número de página (empezando en 1)
 * @param pageSize - Notificaciones por página
 * @param unreadOnly - Si true, solo muestra no leídas
 */
export function useNotifications(
  page: number = 1,
  pageSize: number = 20,
  unreadOnly: boolean = false
) {
  return useQuery<NotificationListResponse>({
    queryKey: notificationKeys.list(page, pageSize, unreadOnly),
    queryFn: () => notificationsApi.list(page, pageSize, unreadOnly),
    staleTime: 30000, // 30 segundos
    refetchInterval: 60000, // Refetch cada minuto
  });
}

/**
 * Hook para obtener el contador de notificaciones no leídas.
 * Se actualiza automáticamente cada 30 segundos.
 */
export function useUnreadCount() {
  return useQuery<UnreadCountResponse>({
    queryKey: notificationKeys.unreadCount(),
    queryFn: notificationsApi.getUnreadCount,
    staleTime: 15000, // 15 segundos
    refetchInterval: 30000, // Refetch cada 30 segundos
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook para marcar una notificación como leída.
 * Invalida las queries de lista y contador.
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) =>
      notificationsApi.markAsRead(notificationId),
    onSuccess: () => {
      // Invalidar todas las queries de notificaciones y el contador
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    onError: () => {
      showToast.error("Error al marcar la notificación");
    },
  });
}

/**
 * Hook para marcar todas las notificaciones como leídas.
 * Invalida las queries de lista y contador.
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: () => {
      // Invalidar todas las queries de notificaciones
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      showToast.success("Todas las notificaciones marcadas como leídas");
    },
    onError: () => {
      showToast.error("Error al marcar las notificaciones");
    },
  });
}

/**
 * Hook para eliminar una notificación.
 * Invalida las queries de lista y contador.
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) =>
      notificationsApi.delete(notificationId),
    onSuccess: () => {
      // Invalidar todas las queries de notificaciones
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      showToast.success("Notificación eliminada");
    },
    onError: () => {
      showToast.error("Error al eliminar la notificación");
    },
  });
}
